import { useEffect, useState } from "react";
import {
  TrashIcon,
  PlusIcon,
  CheckCircleIcon,
  DownloadIcon,
} from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";
import {
  cancelInvitation,
  getInvitationsByEventId,
  sendInvitation,
} from "../../../../api/invitations";
import ToastContainer from "../../../../components/toastContainer";
import Toast from "../../../../components/toast";
import PageLoader from "../../../../components/pageLoader";
import InvitationsFormModal from "./invitationsFormModal";
import ConfirmDeleteModal from "./InvitationCancelModal";

const InvitationsTab = ({ eventId }) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const [activeTab, setActiveTab] = useState("completed"); // active | cancelled

  const LIMIT = 10;

  const [invitations, setInvitations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [deleteInvitationId, setDeleteInvitationId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalPages = Math.ceil(total / LIMIT);

  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState(null);

  const getPageNumbers = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const filteredInvitations = invitations.filter((inv) => {
    if (activeTab === "cancelled") {
      return inv.status === "cancelled";
    }
    return inv.status !== "cancelled";
  });

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      setError("");

      const offset = (page - 1) * LIMIT;
      const data = await getInvitationsByEventId(LIMIT, offset, eventId);
      setInvitations(data.results || []);
      setTotal(data.count || 0);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.detail ||
          "Failed to load invitations.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInvitationSubmit = async (payload) => {
    try {
      setSaving(true);
      await sendInvitation(payload);
      setShowAddModal(false);

      setToast({
        type: "success",
        message: "Invitation sent successfully",
      });

      fetchInvitations();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteInvitation = async () => {
    try {
      setDeleting(true);
      await cancelInvitation(deleteInvitationId);

      setDeleteInvitationId(null);

      fetchInvitations();
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || "Failed to cancel the invitation",
      });
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchInvitations();
  }, [page]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">Invitations</p>
        </div>

        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
        >
          <PlusIcon className="w-4 h-4" />
          Generate Invitation
        </button>
      </div>

      {error && <div className="p-6 text-red-400">{error}</div>}

      <div className="flex gap-4 border-b border-white/10">
        <button
          onClick={() => setActiveTab("completed")}
          className={`pb-2 text-sm font-medium transition ${
            activeTab === "completed"
              ? "text-white border-b-2 border-pink-600"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Active
        </button>

        <button
          onClick={() => setActiveTab("cancelled")}
          className={`pb-2 text-sm font-medium transition ${
            activeTab === "cancelled"
              ? "text-white border-b-2 border-pink-600"
              : "text-slate-400 hover:text-white"
          }`}
        >
          Cancelled
        </button>
      </div>

      {/* Table */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl">
        <div className="overflow-x-auto">
          <table className="min-w-[900px] w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-center text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-4">Number</th>
                <th className="px-6 py-4">Details</th>
                <th className="px-6 py-4">Ticket Type</th>
                <th className="px-6 py-4">Conditional Entry</th>
                <th className="px-6 py-4">Created at/ by</th>
                <th className="px-6 py-4">Check In</th>
                <th className="px-6 py-4">Ticket PDF</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredInvitations.map((inv) => (
                <tr
                  key={inv.id}
                  className="border-b text-center border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {inv.reference}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-slate-300">
                      {inv.full_name} <br /> {inv.email}
                    </div>
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {inv.ticket_type}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {inv.conditional_entry_enabled ? "Yes" : "No"}
                  </td>

                  <td className="px-6 py-4 text-sm text-slate-300">
                    {formatDate(inv.created_at)} <br />{" "}
                    {inv.created_by?.first_name} {inv.created_by?.last_name}
                  </td>

                  <td className="px-6 py-4 text-sm text-center">
                    <CheckCircleIcon
                      className={`w-5 h-5 mx-auto ${
                        inv.is_used ? "text-green-500" : "text-slate-500"
                      }`}
                    />
                  </td>

                  <td className="px-6 py-4 text-center">
                    {inv.pdf_ticket ? (
                      <a
                        href={inv.pdf_ticket}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                        className="text-slate-300 hover:text-white"
                      >
                        <DownloadIcon className="w-5 h-5 mx-auto" />
                      </a>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </td>

                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setDeleteInvitationId(inv.id)}
                        className="p-2 rounded-md text-slate-300 hover:text-red-400 hover:bg-red-500/10 transition"
                        title="Delete company"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state placeholder (future) */}

        {filteredInvitations.length === 0 && (
          <div className="p-8 text-center text-slate-400">
            No {activeTab} invitations found.
          </div>
        )}
      </div>

      <div className="flex justify-end">
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 text-sm text-slate-400">
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 px-6 py-4 text-sm text-slate-400">
                {/* Previous */}
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === 1
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex gap-1">
                  {getPageNumbers().map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => setPage(pageNumber)}
                      className={`px-3 py-1 rounded-md border border-white/10 transition
            ${
              page === pageNumber
                ? "bg-pink-600 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>

                {/* Next */}
                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-3 py-1 rounded-md border border-white/10 transition
        ${
          page === totalPages
            ? "opacity-50 cursor-not-allowed"
            : "hover:bg-slate-800 text-white"
        }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <InvitationsFormModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
        }}
        onSubmit={handleInvitationSubmit}
        eventId={eventId}
        loading={saving}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteInvitationId}
        description="Are you sure you want to cancel this invitation?  This action cannot be undone."
        onClose={() => setDeleteInvitationId(null)}
        onConfirm={handleDeleteInvitation}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default InvitationsTab;
